/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import accordionStyles from './sh-accordion.lit.scss.js';

/**
 * @prop {String} label - The label of the accordion that will be shown at it's top. Mandatory property.
 * @prop {String} info - The info of the accordion that will be shown next to label.
 * @prop {Boolean} expanded - Defines whether the accordion is open or closed. Can be set to true to be open by default.
 * @prop {Boolean} flat - If set to `true`, the accordion will have no background color nor elevation applied to it.
 * @prop {String} icon - Optional property that reflects the icon of the accordion.
 * @prop {String} iconColor - Defines the color of the icon when icon is set.
 * @prop {Boolean} disabled - If set to `true`, the accordion will be disabled.
 * @prop {String} labelRows - Default auto.Allows longer labels to be used.
 * @attr {String} label-rows - Default auto.Allows longer labels to be used.
 * @slot - The slot where the main content is rendered.
 * @slot Takes text as children.This slot is only provided for labels that need to be styled, e.g. text highlight. For that, the sh-text that goes in there should have the same type as the label, that is Header 2. No other property, such as 'icon' and 'information' can be used together with this slot.
 * @slot footer - Takes buttons as children.
 */

export class SHAccordion extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      info: { type: String, reflect: true },
      flat: { type: Boolean, reflect: true },
      expanded: { type: Boolean, reflect: true },
      icon: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
      _emptyFooter: { type: Boolean, reflect: true },
      _emptyHeader: { type: Boolean, reflect: true },
      iconColor: { type: String, reflect: true, attribute: 'icon-color' },
      labelRows: { type: String, reflect: true, attribute: 'label-rows' },
    };
  }

  static get styles() {
    return [sharedStyles, accordionStyles];
  }

  render() {
    return html`
      <div class="accordion-wrapper">
        <div class="header-wrapper" @click="${() => this._handleExpand()}">
          ${this.icon && this._emptyHeader
            ? html` <sh-icon
                size="s"
                .icon="${this.icon}"
                .color="${this.iconColor}"
                class="accordion-icon"
                ?disabled="${this.disabled}"
              ></sh-icon>`
            : ''}
          ${this._emptyHeader
            ? html` <sh-text size="header-2" class="accordion-label">${this.label}</sh-text>
                <sh-text size="body-1" color="secondary" class="accordion-info"
                  >${this.info}</sh-text
                >`
            : ''}
          <div class="header-slot-wrapper" ?empty-header=${this._emptyHeader}>
            <slot
              name="header"
              class="header"
              @slotchange="${(e) => this._updateHeaderSlotFlag(e.target)}"
            ></slot>
          </div>
          <sh-icon
            class="expand accordion-expand"
            icon="arrow-down-s"
            button
            ?disabled="${this.disabled}"
          ></sh-icon>
        </div>
        <div class="content-wrapper">
          <slot></slot>
          <div class="footer-wrapper" ?empty-footer=${this._emptyFooter}>
            <slot
              name="footer"
              class="footer"
              @slotchange="${(e) => this._updateSlotFlag(e.target)}"
            ></slot>
          </div>
        </div>
      </div>
    `;
  }
  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('no-hovermq');
    }
  }
  updated(changedProperties) {
    if (changedProperties.has('disabled')) {
      this._handleDisabled();
    }
    const listOfProperties = ['label', 'info', 'flat', 'expanded', 'icon', 'disabled'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  constructor() {
    super();
    this.label = 'Accordion Label';
    this._emptyFooter = true;
    this._emptyHeader = true;
  }

  _updateSlotFlag(slot) {
    const footerNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    if (footerNodes === 0) {
      this._emptyFooter = true;
    } else {
      this._emptyFooter = false;
    }
  }

  _updateHeaderSlotFlag(slot) {
    const headerNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    this._emptyHeader = headerNodes === 0;
  }

  _handleExpand() {
    this.expanded = !this.expanded;
  }

  _handleDisabled() {
    if (this.disabled) {
      this.expanded = false;
    }
  }
}

customElements.define('sh-accordion', SHAccordion);
